'use client'
import noDataImg from '@/assets/images/error-images/results.svg'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js'
import React, { useEffect } from 'react'
import { Bar, Line } from 'react-chartjs-2'
import NoData from '../noData'
import SpiderStats from '../spiderStats'
import WagonWheel from '../spiderStats/wagonWheel'
import WinPercentage from '../winPercentage'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)
function StatGraphs({ graphData, activeTab, matchDetail, inningData }: any) {
  const obj: Record<string | number, any> = {}
  graphData.forEach((item: any) => {
    obj[item.nInningNumber] = item
  })
  const [theme, setTheme] = React.useState<any>(window.localStorage.getItem('cb-theme') || 'light')

  const inningsData = Object.values(obj)

  const darkColors = ['#4A6E31', '#802D41', '#61DE7D', '#EFA007']
  const lightColors = ['#B1D98C', '#CA687F', '#61DE7D', '#EFA007']

  const labels = []
  const temp: any = []

  inningsData.forEach((inning, index) => {
    labels.push(inning.sName)
    temp.push({
      label: inning.sName,
      data:
        activeTab === 'aManhattan'
          ? inning[activeTab]?.map((runType: any) => {
              return {
                run: parseInt(runType.nRuns),
                wicket: parseInt(runType?.nWickets),
                inning: inning?.nInningNumber
              }
            })
          : inning[activeTab]?.map((runType: any) =>
              parseInt(
                activeTab === 'aWorm'
                  ? runType.nRuns
                  : activeTab === 'aRunRates'
                    ? runType.nRunRate
                    : runType.sValue
              )
            ),
      backgroundColor: lightColors[index],
      borderColor: lightColors[index],
      borderWidth: activeTab === 'aWorm' ? 4 : 1,
      categoryPercentage: 0.9,
      barPercentage: 0.4,
      borderRadius: 1
    })
  })

  const [datasets, setDatasets] = React.useState<any>(temp)

  function getNewInningData() {
    const themeToUse = window.localStorage.getItem('cb-theme') || 'light'
    const d: any = []
    setTheme(themeToUse)

    inningsData.forEach((inning, index) => {
      labels.push(inning.sName)
      d?.push({
        label: inning.sName,
        data:
          activeTab === 'aManhattan'
            ? inning[activeTab]?.map((runType: any) => ({
                run: parseInt(runType.nRuns),
                wicket: parseInt(runType?.nWickets),
                inning: inning?.nInningNumber
              }))
            : inning[activeTab]?.map((runType: any) =>
                parseInt(
                  activeTab === 'aWorm'
                    ? runType.nRuns
                    : activeTab === 'aRunRates'
                      ? runType.nRunRate
                      : runType.sValue
                )
              ),
        backgroundColor: themeToUse === 'light' ? lightColors[index] : darkColors[index],
        borderColor: themeToUse === 'light' ? lightColors[index] : darkColors[index],
        borderWidth: activeTab === 'aWorm' ? 4 : 1,
        categoryPercentage: 0.9,
        barPercentage: 1,
        maxBarThickness: 50,
        borderRadius: 1
      })
    })

    setDatasets(d)
  }

  useEffect(() => {
    getNewInningData()
    const handleThemeChange = () => {
      getNewInningData()
    }

    window.addEventListener('themeChanged', handleThemeChange)

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange)
    }
  }, [activeTab])

  const uniqueRunTypes = new Set()

  inningsData.forEach(inning => {
    inning[activeTab]?.forEach((runType: any) => {
      uniqueRunTypes.add(
        activeTab === 'aManhattan' || activeTab === 'aWorm'
          ? runType.nOver
          : activeTab === 'aRunRates'
            ? runType.nOver
            : runType.sName
      )
    })
  })

  const data: any = {
    labels: Array.from(uniqueRunTypes),
    datasets: datasets
  }

  const noData = data?.datasets?.every(
    (set: { data: number[] }) => !set?.data?.length || set?.data?.every(item => item === 0)
  )

  const horizontalOptions = {
    plugins: {
      legend: {
        display: true,

        position: 'top',
        align: 'end',
        labels: {
          borderRadius: 4,
          font: {
            size: 16
          },
          color: '#999999',
          boxWidth: 16
        }
      },
      datalabels: {
        color: theme === 'light' ? '#1E252B' : '#DFE1E2',
        font: {
          weight: 400,
          size: 10
        },
        display: false
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'x',
    scales: {
      x: {
        title: {
          display: true,

          // text: 'Overs',
          align: 'end'
        },
        grid: {
          display: true,
          color: theme === 'light' ? '#DFE1E2' : '#1E252B'
        },
        ticks: {
          stepSize: 6,
          beginAtZero: true
        }
      },
      y: {
        title: {
          display: true,
          align: 'end'
        },
        grid: {
          display: true,
          color: (ctx: any) => {
            return 'index' in ctx ? 'transparent' : '#272D2F'
          }
        }
      }
    }
  }

  const verticalOptions = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'x',
    plugins: {
      legend: {
        display: true,

        position: 'top',
        align: 'end',
        labels: {
          borderRadius: 4,
          font: { size: 16 },
          color: '#999999',
          boxWidth: 16
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const datasetIndex = context.datasetIndex
            const index = context.dataIndex
            const innings = data?.datasets[datasetIndex]
            const item = innings.data[index]
            if (activeTab === 'aManhattan') {
              return [`${innings.label}`, `Runs: ${item.run}`, `Wickets: ${item.wicket}`]
            } else {
              return [`${innings.label}`]
            }
          }
        }
      },
      datalabels: {
        color: theme === 'light' ? '#1E252B' : '#DFE1E2',
        font: {
          weight: 400,
          size: 10
        },
        display: false
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Overs',
          align: 'end'
        },
        grid: {
          display: true,
          color: (ctx: any) => (ctx.index !== 0 ? 'transparent' : '#272D2F')
        },
        ticks: {
          maxTicksLimit: 20
        }
      },
      y: {
        title: {
          display: true,
          text: 'Runs',
          align: 'end'
        },
        ticks: {
          stepSize: 6,
          beginAtZero: true
        },
        grid: {
          display: true,
          color: theme === 'light' ? '#DFE1E2' : '#1E252B'
        }
      }
    }
  }

  // Transform data for Chart.js
  const chartData = {
    labels: data.labels,
    datasets: data?.datasets?.map((dataset: any) => ({
      label: dataset.label,
      data: dataset?.data?.map((item: any) => item.run),
      backgroundColor: dataset.backgroundColor,
      borderColor: dataset.borderColor,
      borderWidth: dataset.borderWidth,
      categoryPercentage: dataset.categoryPercentage,
      barPercentage: dataset.barPercentage,
      parsing: {
        yAxisKey: 'run'
      }
    }))
  }

  // Then update your verticalOptions for Manhattan chart
  const manhattanOptions = {
    ...verticalOptions,
    plugins: {
      ...verticalOptions.plugins,
      datalabels: {
        align: 'end',
        anchor: 'end',
        offset: 4,
        formatter: function (value: any, context: any) {
          // Get the dataset and index
          const datasetIndex = context.datasetIndex
          const index = context.dataIndex

          // Get the wicket count (assuming data structure has wicket property)
          const dataPoint = data.datasets[datasetIndex].data[index]
          const wickets = dataPoint.wicket

          // Return wicket symbols if there are any wickets, otherwise return empty string
          return wickets > 0 ? '🎾' : ''
        },
        color: function () {
          return theme === 'light' ? '#802D41' : '#CA687F'
        },
        font: {
          weight: 'bold',
          size: 12
        },
        display: false
      }
    }
  }

  function filterDataForWickets(data: { datasets: { data: number[] }[]; labels: string[] }): any {
    const indicesToRemove: number[] = []
    for (const datasetIndex in data?.datasets?.[0].data) {
      const isRemovable = data?.datasets?.every(
        (dataset: { data: number[] }) => dataset?.data[Number(datasetIndex)] === 0
      )
      if (isRemovable) {
        indicesToRemove.push(Number(datasetIndex))
      }
    }
    const filteredData = {
      ...data,
      datasets: data?.datasets?.map((dataset: any) => ({
        ...dataset,
        data: dataset?.data?.filter((_: number, index: number) => !indicesToRemove?.includes(index))
      })),
      labels: data?.labels?.filter((_: string, index: number) => !indicesToRemove?.includes(index))
    }
    return filteredData
  }

  const renderGraph = () => {
    switch (activeTab) {
      case 'aManhattan':
        return (
          <div className="md:w-full">
            <Bar options={manhattanOptions as any} data={chartData} />
          </div>
        )
      // case 'aPartnership':
      //   return <Partnership graphData={graphData} is100={is100} theme={theme} />
      case 'aWorm':
        return (
          <div className="md:w-full">
            {!noData ? (
              <Line options={verticalOptions as any} data={data} />
            ) : (
              <NoData imagePath={noDataImg} title="Data will appear once the match progresses" />
            )}
          </div>
        )
      case 'aRunTypes': {
        const filteredData = filterDataForWickets(data)
        return (
          <div className="md:w-full">
            {!noData ? (
              <Bar options={horizontalOptions as any} data={filteredData} />
            ) : (
              <NoData imagePath={noDataImg} title="Data will appear once the match progresses" />
            )}
          </div>
        )
      }
      case 'aWickets': {
        const filteredData = filterDataForWickets(data)
        return (
          <div className="md:w-full">
            {!noData ? (
              <Bar options={horizontalOptions as any} data={filteredData} />
            ) : (
              <NoData imagePath={noDataImg} title="Data will appear once the match progresses" />
            )}
          </div>
        )
      }
      case 'aExtras': {
        const filteredData = filterDataForWickets(data)
        return (
          <div className="md:w-full">
            {!noData ? (
              <Bar options={horizontalOptions as any} data={filteredData} />
            ) : (
              <NoData imagePath={noDataImg} title="Data will appear once the match progresses" />
            )}
          </div>
        )
      }
      case 'aRunRates':
        return (
          <div className="md:w-full">
            {!noData ? (
              <Line options={verticalOptions as any} data={data} />
            ) : (
              <NoData imagePath={noDataImg} title="Data will appear once the match progresses" />
            )}
          </div>
        )
      case 'spider':
        return <SpiderStats matchDetail={matchDetail} inningData={inningData} />
      case 'wagonWheel':
        return <WagonWheel matchDetail={matchDetail} inningData={inningData} />
      case 'winPercentage':
        return <WinPercentage matchDetail={matchDetail} />
      default:
        return null
    }
  }

  return <div key={`${theme}-${activeTab}`}>{renderGraph()}</div>
}

export default StatGraphs
